import {Component} from '@angular/core';
import {FinanceService} from '../../../finance/finance.service';
import {BankAccount} from '../../../shared/models/bank-account.model';
import {MdDialogRef, MdDialog, MdDialogConfig} from '@angular/material';
import {SnackBarService} from '../../../services/snackbar.service';
import {BankStatementController} from './bank-statement.controller';
import {DuplicateDocumentWarningComponent} from '../duplicate-document-warning/duplicate-document-warning.component';

@Component({
  selector: 'app-add-bank-statement',
  templateUrl: './add-bank-statement.component.html',
  styleUrls: ['./add-bank-statement.component.css']
})
export class AddBankStatementComponent {
  fileName: string;
  documentAnalysisResponse: any;
  file: File;
  loading = false;
  loadingText = 'Saving document.';
  selectedBankAccount: BankAccount;
  fileLoaded: boolean;
  private currentBankStatementId: number;

  constructor(
    private financeService: FinanceService,
    private dialogRef: MdDialogRef<any>,
    private snackBarService: SnackBarService,
    private bankStatementController: BankStatementController,
    private dialog: MdDialog
    ) {
  }

  onDocumentSelected($event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.fileName = input.files[0].name;
      this.file = input.files[0];
      this.fileLoaded = true;
    }
  }

  saveDocument() {
    this.loading = true;
    this.bankStatementController
      .saveBankStatement(this.file, this.selectedBankAccount.accountId, (response: any, success: boolean) => {
        if (success) {
          // Check if this is a duplicate document
          if (response.duplicate === true) {
            this.loading = false;
            this.showDuplicateWarning(response);
          } else {
            this.currentBankStatementId = response.id;
            this.processToAnalyzeDocument(response);
          }
        } else {
          this.loading = false;
        }
    });
  }

  private showDuplicateWarning(duplicateInfo: any) {
    const dialogConfig = new MdDialogConfig();
    dialogConfig.width = '500px';
    dialogConfig.data = {
      duplicateInfo: duplicateInfo,
      bankAccount: this.selectedBankAccount,
      tenantId: duplicateInfo.tenantId
    };
    
    const warningDialog = this.dialog.open(DuplicateDocumentWarningComponent, dialogConfig);
    warningDialog.afterClosed().subscribe((result: any) => {
      if (result) {
        if (result.action === 'continue') {
          // User wants to continue - use the existing document ID
          this.currentBankStatementId = result.duplicateId;
          this.processToAnalyzeDocument(duplicateInfo);
        } else if (result.action === 'cancel') {
          // User cancelled
          this.dialogRef.close(false);
        }
      } else {
        // Dialog closed without action
        this.dialogRef.close(false);
      }
    });
  }

  private processToAnalyzeDocument(response: any) {
    this.loadingText = 'Starting document analysis';
    
    this.bankStatementController
      .getDocumentAnalysis(
        response.uniqueDocumentName,
        this.selectedBankAccount.accountId,
        response.tenantId,
        (responseRequest: any, analyzedDocument: boolean) => {
          // Check if response contains status information
          if (responseRequest && responseRequest.status) {
            // Backend returned status (processing, failed, etc.)
            if (responseRequest.status === 'processing') {
              // Processing started asynchronously, update local state and close dialog
              if (response.id) {
                response.processingState = 'processing';
                response.processingStartDate = responseRequest.processingStartDate || Date.now();
              }
              this.dialogRef.close(true);
              this.loading = false;
              return;
            } else if (responseRequest.status === 'failed') {
              // Analysis failed to start
              this.snackBarService.showSimpleMsj('Failed to start document analysis: ' + (responseRequest.message || 'Unknown error'));
              if (response.id) {
                response.processingState = 'failed';
              }
              this.dialogRef.close(true);
              this.loading = false;
              return;
            }
          }
          
          // Legacy synchronous processing (no status field)
          if (analyzedDocument && responseRequest) {
            this.processToSaveJsonData(responseRequest);
          } else {
            this.dialogRef.close(true);
            this.loading = false;
          }
        },
        response.id // Pass bankStatementId for async processing
    );
  }

  private processToSaveJsonData(response: any) {
    this.loadingText = 'Saving document info extracted';
    this.bankStatementController.saveJsonObject(response, this.currentBankStatementId, (success: boolean) => {
      if (success) {
        // Check for duplicates before pushing
        this.loadingText = 'Checking for duplicate transactions';
        this.financeService.checkDuplicates(this.currentBankStatementId).subscribe(
          (duplicateResponse: any) => {
            const duplicates = (duplicateResponse.arrResult && duplicateResponse.arrResult.duplicates) || duplicateResponse.duplicates || [];
            if (duplicates.length > 0) {
              // Show warning about duplicates (for now, just log - UI can be enhanced later)
              const message = `Warning: ${duplicates.length} duplicate transaction(s) detected. They will be skipped during import.`;
              this.snackBarService.showSimpleMsj(message);
            }
            // Auto-execute Push TM after analysis completes
            this.loadingText = 'Pushing transactions to Transaction Monitoring';
            this.financeService.pushToTransactionMonitoring(this.currentBankStatementId).subscribe(
              (pushResponse: any) => {
                if (pushResponse && pushResponse.arrResult) {
                  const result = pushResponse.arrResult;
                  const message = `Successfully pushed ${result.created || 0} transactions to Transaction Monitoring. ${result.skipped || 0} transactions were already pushed.`;
                  this.snackBarService.showSimpleMsj(message);
                } else {
                  this.snackBarService.showSimpleMsj('Successfully pushed transactions to Transaction Monitoring');
                }
                this.dialogRef.close(success);
              },
              error => {
                this.snackBarService.showSimpleMsj('Warning: Transactions saved but failed to push to Transaction Monitoring: ' + (error.message || 'Unknown error'));
                this.dialogRef.close(success);
              }
            );
          },
          error => {
            // If duplicate check fails, still proceed with push
            this.loadingText = 'Pushing transactions to Transaction Monitoring';
            this.financeService.pushToTransactionMonitoring(this.currentBankStatementId).subscribe(
              (pushResponse: any) => {
                if (pushResponse && pushResponse.arrResult) {
                  const result = pushResponse.arrResult;
                  const message = `Successfully pushed ${result.created || 0} transactions to Transaction Monitoring. ${result.skipped || 0} transactions were already pushed.`;
                  this.snackBarService.showSimpleMsj(message);
                } else {
                  this.snackBarService.showSimpleMsj('Successfully pushed transactions to Transaction Monitoring');
                }
                this.dialogRef.close(success);
              },
              pushError => {
                this.snackBarService.showSimpleMsj('Warning: Transactions saved but failed to push to Transaction Monitoring: ' + (pushError.message || 'Unknown error'));
                this.dialogRef.close(success);
              }
            );
          }
        );
      } else {
        console.log('error al guardar')
        this.dialogRef.close(true);
      }
    });
  }
}






// WEBPACK FOOTER //
// ./src/app/entities/finance/bank-statement/add-bank-statement.component.ts